import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { AnimatePresence, motion as m } from "framer-motion";
import PageHeader from "../components/PageHeader";
import ArticleCard from "../components/ArticleCard";
import EditorialCover from "../components/EditorialCover";
import { Button, Container, Reveal, Section, SectionHeading } from "../components/ui";
import { articleCategories, articles, featuredArticles } from "../data/articles";

const Blog = () => {
  const [category, setCategory] = useState("All");
  const lead = featuredArticles[0] || articles[0];

  const filtered = useMemo(
    () =>
      category === "All"
        ? articles
        : articles.filter((a) => a.category === category),
    [category]
  );

  const counts = useMemo(() => {
    const map = { All: articles.length };
    articles.forEach((a) => {
      map[a.category] = (map[a.category] || 0) + 1;
    });
    return map;
  }, []);

  return (
    <>
      <Helmet>
        <title>Writing — Jephthah Boateng</title>
        <meta
          name="description"
          content="Essays on engineering practice — data pipelines, architecture, debugging and craft — written at length rather than in bullet points."
        />
      </Helmet>

      <PageHeader
        eyebrow="Writing"
        title="Thinking out loud, at length."
        lead="Long-form notes on the things I keep running into: pipelines that cannot be re-run, permissions that only exist in the client, aggregates that hide the decision, and deploys nobody wants to do on a Friday."
        meta={[`${articles.length} essays`, "Updated monthly", "No newsletter required"]}
      />

      {/* Lead story */}
      {lead && (
        <Section tone="default" className="!pb-0">
          <Reveal>
            <Link
              to={`/blog/${lead.slug}`}
              className="group grid gap-10 lg:grid-cols-12"
            >
              <div className="overflow-hidden rounded-lg lg:col-span-7">
                <div className="aspect-[16/10]">
                  {lead.img ? (
                    <img
                      src={lead.img}
                      alt={lead.title}
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-standard group-hover:scale-[1.04]"
                    />
                  ) : (
                    <EditorialCover
                      title={lead.title}
                      kicker={lead.category}
                      accent={lead.accent}
                    />
                  )}
                </div>
              </div>
              <div className="flex flex-col justify-center lg:col-span-5">
                <span className="t-eyebrow text-electric">Lead essay</span>
                <h2 className="mt-5 font-display text-4xl leading-[1.05] transition-colors duration-300 group-hover:text-azure dark:group-hover:text-electric md:text-5xl">
                  {lead.title}
                </h2>
                <p className="t-lead mt-5">{lead.dek}</p>
                <p className="mt-7 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-muted dark:text-slightwhite/55">
                  {lead.category} · {lead.readTime} min read
                </p>
              </div>
            </Link>
          </Reveal>
        </Section>
      )}

      {/* Index */}
      <Section tone="default">
        <SectionHeading
          eyebrow="The archive"
          title="Everything, filed."
          align="left"
        />

        <div className="hide-scrollbar mt-10 flex gap-1 overflow-x-auto border-b border-[var(--color-border)] pb-4">
          {articleCategories.map((cat) => {
            const active = cat === category;
            return (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`relative shrink-0 whitespace-nowrap rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors duration-300 ${
                  active
                    ? "text-paper dark:text-paper"
                    : "text-ink-muted hover:text-ink dark:text-slightwhite/55 dark:hover:text-slightwhite"
                }`}
              >
                {active && (
                  <m.span
                    layoutId="blog-filter-pill"
                    className="absolute inset-0 rounded-full bg-azure dark:bg-electric"
                    transition={{ type: "spring", stiffness: 380, damping: 32 }}
                  />
                )}
                <span className="relative">
                  {cat}
                  <span className="ml-2 opacity-60">{counts[cat] || 0}</span>
                </span>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <m.div
            key={category}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="mt-12 grid gap-x-8 gap-y-14 md:grid-cols-2 lg:grid-cols-3"
          >
            {filtered.map((article, i) => (
              <ArticleCard key={article.slug} article={article} index={i} />
            ))}
          </m.div>
        </AnimatePresence>
      </Section>

      <Section tone="warm" className="!py-20">
        <Container className="!px-0 text-center">
          <h2 className="mx-auto max-w-2xl font-display text-4xl leading-tight md:text-5xl">
            Disagree with something here?
          </h2>
          <p className="t-lead mx-auto mt-5 max-w-prose">
            Good. I would rather be corrected than quoted. Tell me where I have it wrong.
          </p>
          <div className="mt-9 flex justify-center">
            <Button to="/contact" withArrow>
              Write to me
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
};

export default Blog;
